import { Dispatch } from "redux"
import { ValidationError } from "sequelize"
import {
    CcComplianceRevision,
    CcComplianceRevisionSave,
    CcComplianceItemState,
    CcComplianceHistory,
    CcComplianceHistoryCreate,
    CcComplianceHistoryUpdateResponse,
    CcComplianceHistoryImportProps,
    CcComplianceHistoryVersion,
    CcComplianceTempAnswer,
    CcComplianceTempAnswerVersion,
} from "server/models"
import { GET, POST, DELETE, validate } from "client/store/utils"
import { LangType } from "client/store"
import {
    ComplianceRevisionValidation,
    ComplianceHistoryValidation,
    ComplianceTempAnswerValidation,

    ComplianceStateList,
    ComplianceHistoryList,
    ComplianceTempAnswerList,
    ComplianceTempAnswerByUser,
    ComplianceTempAnswerVersionByUser,
} from "./types"
import {
    setCcComplianceItemStateList,
    setCcComplianceItemHistoryList,
    setCcComplianceTempAnswerList,
} from "./actions"

// compliance revisions

export const LoadCcComplianceRevisionData = (revision: CcComplianceRevision, dispatch: Dispatch) => (
    Promise.all([
        LoadAllCcComplianceItemState(revision.id).then((res) => {
            dispatch(setCcComplianceItemStateList(revision.setId, res))
        }),
        LoadAllCcComplianceHistory(revision.id).then((res) => {
            dispatch(setCcComplianceItemHistoryList(revision.setId, res))
        }),
        LoadCcComplianceTempAnswerList(revision.id).then((res) => {
            dispatch(setCcComplianceTempAnswerList(revision.setId, res))
        }),
    ])
)
export const LoadCcComplianceRevisions = (setId: number) => (
    GET(`/ccComplianceRevision/setId/${setId}`)
    .then((revisions: CcComplianceRevision[]) => revisions)
)
export const CreateCcComplianceRevision = (setId: number) => (
    GET(`/ccComplianceRevision/create/setId/${setId}`)
    .then((revision: CcComplianceRevision) => revision)
)
export const UpdateCcComplianceRevision = (id: number, name: string, lang: LangType) => {
    const data: CcComplianceRevisionSave = { id, name }
    return POST(
        "/ccComplianceRevision/update",
        JSON.stringify(data),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ComplianceRevisionValidation>validate(error, lang)

        return validation
    })
}
export const DeleteCcComplianceRevision = (revisionId: number) => (
    DELETE(`/ccComplianceRevision/delete/${revisionId}`)
    .then((removedRows: number) => removedRows)
)
export const DeleteAllCcComplianceRevisions = (setId: number) => (
    DELETE(`/ccComplianceRevision/deleteAll/${setId}`)
    .then((removedRows: number) => removedRows)
)

// compliance state

export const LoadAllCcComplianceItemState = (complianceRevisionId: number) => (
    GET(`/ccComplianceItemState/listView/revision/${complianceRevisionId}`)
    .then((stateArray: CcComplianceItemState[]) => {
        const stateList: ComplianceStateList = {}
        stateArray.forEach((state) => {
            stateList[state.itemId] = state
        })
        return stateList
    })
)
export const LoadCcComplianceItemState = (itemId: number, complianceRevisionId: number) => (
    GET(`/ccComplianceItemState/item/${itemId}/revision/${complianceRevisionId}`)
    .then((state: CcComplianceItemState) => state)
)
export const UpdateCcComplianceVerifier = (data: CcComplianceItemState[]) => (
    POST(
        "/ccComplianceItemState/verifier",
        JSON.stringify(data),
    )
)
export const UpdateCcComplianceState = (data: CcComplianceItemState) => (
    POST(
        "/ccComplianceItemState/update",
        JSON.stringify(data),
    )
)
export const CreateCcComplianceState = (data: CcComplianceItemState) => (
    POST(
        "/ccComplianceItemState/create",
        JSON.stringify(data),
    )
)

// compliance history

export const CreateManyCcComplianceHistory = (
    historyArray: CcComplianceHistoryCreate[],
) => (
    POST(
        "/ccComplianceHistory/bulkCreate",
        JSON.stringify(historyArray),
    )
)
export const CreateCcComplianceHistory = (
    history: CcComplianceHistoryCreate,
    lang: LangType,
) => (
    POST(
        "/ccComplianceHistory/create",
        JSON.stringify(history),
    ).then((res) => {
        const newHistory = res as CcComplianceHistory
        const error = res as ValidationError
        const validation = <ComplianceHistoryValidation>validate(error, lang)

        return {
            newHistoryId: newHistory.id || null,
            validation,
        }
    })
)
export const CreateCcComplianceHistoryVersions = (versions: CcComplianceHistoryVersion[]) => (
    POST(
        "/ccComplianceHistoryVersion/save",
        JSON.stringify(versions),
    )
)
export const UpdateCcComplianceHistoryResponse = ( // for Super Admin only
    id: number,
    responseId: number,
    answer: string,
    lang: LangType,
) => {
    const data: CcComplianceHistoryUpdateResponse = {
        id,
        responseId,
        answer,
    }
    return POST(
        "/ccComplianceHistory/updateResponse",
        JSON.stringify(data),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ComplianceHistoryValidation>validate(error, lang)
        return validation
    })
}
export const LoadCcComplianceHistory = (itemId: number, complianceRevisionId: number) => (
    GET(`/ccComplianceHistory/item/${itemId}/revision/${complianceRevisionId}`)
    .then((history: CcComplianceHistory[]) => history)
)
export const LoadAllCcComplianceHistory = (complianceRevisionId: number) => (
    GET(`/ccComplianceHistory/revision/${complianceRevisionId}`)
    .then((historyArray: CcComplianceHistory[]) => {
        const historyList: ComplianceHistoryList = {}
        historyArray.forEach((history) => {
            if (!historyList[history.itemId]) historyList[history.itemId] = []
            historyList[history.itemId].push(history)
        })
        return historyList
    })
)
export const ImportCcComplianceHistory = (data: CcComplianceHistoryImportProps) => (
    POST(
        "/ccComplianceHistory/import",
        JSON.stringify(data),
    )
)

// temp answer

export const LoadCcComplianceTempAnswerList = (complianceRevisionId: number) => (
    GET(`/ccComplianceTempAnswer/revision/${complianceRevisionId}`)
    .then((answers: CcComplianceTempAnswer[]) => {
        const answerList: ComplianceTempAnswerList = {}
        answers.forEach((answer) => {
            if (!answerList[answer.itemId]) answerList[answer.itemId] = {}
            answerList[answer.itemId][answer.editorId] = answer
        })
        return answerList
    })
)
export const LoadCcComplianceTempAnswers = (
    itemId: number,
    complianceRevisionId: number,
) => (
    GET(`/ccComplianceTempAnswer/item/${itemId}/revision/${complianceRevisionId}`)
    .then((answers: CcComplianceTempAnswer[]) => {
        const tempAnswers: ComplianceTempAnswerByUser = {}
        answers.forEach((answer) => {
            tempAnswers[answer.editorId] = answer
        })
        return tempAnswers
    })
)
export const SaveCcComplianceTempAnswer = (
    answer: CcComplianceTempAnswer,
    lang: LangType,
) => (
    POST(
        "/ccComplianceTempAnswer/save",
        JSON.stringify(answer),
    ).then((res) => {
        const error = res as ValidationError
        const validation = <ComplianceTempAnswerValidation>validate(error, lang)
        return validation
    })
)
export const DeleteCcComplianceTempAnswers = (
    itemId: number,
    complianceRevisionId: number,
) => (
    DELETE(`/ccComplianceTempAnswer/delete/item/${itemId}/revision/${complianceRevisionId}`)
    .then((removedRows: number) => removedRows)
)

// temp answer versions

export const LoadCcComplianceTempAnswerVersions = (
    itemId: number,
    complianceRevisionId: number,
) => (
    GET(`/ccComplianceTempAnswerVersion/item/${itemId}/revision/${complianceRevisionId}`)
    .then((versions: CcComplianceTempAnswerVersion[]) => {
        const tempVersions: ComplianceTempAnswerVersionByUser = {}
        versions.forEach((version) => {
            if (!tempVersions[version.editorId]) tempVersions[version.editorId] = []
            tempVersions[version.editorId].push(version)
        })
        return tempVersions
    })
)
export const SaveCcComplianceTempAnswerVersions = (versions: CcComplianceTempAnswerVersion[]) => (
    POST(
        "/ccComplianceTempAnswerVersion/save",
        JSON.stringify(versions),
    )
)
export const DeleteCcComplianceTempAnswerVersions = (
    itemId: number,
    complianceRevisionId: number,
    editorId: number,
) => (
    DELETE(`/ccComplianceTempAnswerVersion/delete/item/${itemId}/revision/${complianceRevisionId}/editor/${editorId}`)
    .then((removedRows: number) => removedRows)
)
